import React, { useContext, useEffect, useState } from 'react';
import { Container, useStepperStyles, FakeButton } from './MortgageApplicationPage.styles';
import { useAuthorization } from 'core/context';
import { 
    getUser,
    getApplications,
    getCompleteApplication
} from 'core/api';
import {
    Introduction,
    MortgageType,
    Employment,
    Financial,
    Mortgage,
    Submitted
} from './steps';
import { useHistory } from 'react-router';
import { AuthState } from 'core/context/reducer';
import { IApplication } from 'core/utilities';
import { useToast } from 'core/toast';
import { Spinner } from 'view/components/layout';
import { MobileStepper } from '@material-ui/core';
import { IProgressContext, ProgressContext } from 'provider/ProgressContext';

export interface StepProps
{
    step            : number;
    setStep         : React.Dispatch<React.SetStateAction<number>>;
    progressForward : () => void;
    progressBackward: () => void;
    setMortgageType : React.Dispatch<React.SetStateAction<ApplicationType | undefined>>;
    mortgageType?   : ApplicationType;
    authState       : AuthState;
    application     : IApplication;
    complete        : any;
    refresh         : () => void;
}

const applicationSteps = [
    Introduction,
    MortgageType,
    Employment,
    Financial,
    Mortgage,
    Submitted
] as Array<React.FunctionComponent<StepProps>>;

export enum ApplicationType
{
    PRE_APPROVAL = 'p',
    NEW_PURCHASE = 'n',
    TRANSFER     = 't'
}

export const MortgageApplicationPage : React.FunctionComponent = () => {
    const history = useHistory();
    const { setToastMessage } = useToast();
    const authState = useAuthorization();
    const [ mortgageType, setMortgageType ] = useState<ApplicationType | undefined>();
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ application, setApplication ] = useState<any>();
    const [ complete, setComplete ] = useState<any>();
    const [ refetch, setRefetch ] = useState<number>(0);
    const [ step, setStep ] = useState<number>(0);
    const Step = applicationSteps[step];
    const classes = useStepperStyles();
    const { progressBarStep, progressForward, progressBackward, resetProgress } = useContext(ProgressContext) as IProgressContext;
    const [ totalProgressSteps, setTotalProgressSteps ] = useState<number>(15);

    useEffect(() => {
        let fetchSucceeded = false;
        let fetchTimedOut = false;

        (async () => {
            const { user } = await getUser(authState);
            
            if (user?.waitlist && user?.waitlist > -1) {
                // Users are not able to edit their application once it has been submitted
                setStep(5); // Go to submitted screen :)
                setIsLoading(false);
            } else {
                const { application } = await getApplications(authState);
                const { complete } = await getCompleteApplication(authState, application?.id ?? '');

                if (!fetchTimedOut) {
                    // If application is not new (i.e. in 'underwriting', 'review', etc)
                    // disallow changes to their application
                    if (application?.status !== 'n') history.push('/dashboard');

                    fetchSucceeded = true;
                    setApplication(application);
                    setComplete(complete);
                    setIsLoading(false);
                }
            }
        })();

        // Notify & redirect the user if fetching their application takes longer than 60s
        const timeoutId = setTimeout(() => {
            if (!fetchSucceeded) {
                fetchTimedOut = true;
                setToastMessage();
                history.push('/dashboard');
            }
        }, 1000 * 60);

        return (() => clearTimeout(timeoutId));
    }, [ step, refetch ]);

    useEffect(() => {
        if (mortgageType === ApplicationType.PRE_APPROVAL) setTotalProgressSteps(11);
        if (mortgageType === ApplicationType.NEW_PURCHASE) setTotalProgressSteps(14);
        if (mortgageType === ApplicationType.TRANSFER) setTotalProgressSteps(13);
    }, [ mortgageType ]);

    useEffect(() => {
        resetProgress();
    }, []);

    const refresh = () => setRefetch(r => r + 1);

    return (
        <>
            {isLoading && <Spinner css='position: absolute; padding-top: 116px;' />}
            <Container isLoading={isLoading}>
                <MobileStepper 
                    variant='progress'
                    steps={totalProgressSteps}
                    activeStep={progressBarStep}
                    classes={{ root: classes.root, progress: classes.progress }}
                    LinearProgressProps={{ classes: { barColorPrimary: classes.bar } }}
                    backButton={<FakeButton>Back</FakeButton>}
                    nextButton={<FakeButton>Next</FakeButton>}
                />
                <Step 
                    step={step}
                    setStep={setStep}
                    progressForward={progressForward}
                    progressBackward={progressBackward}
                    setMortgageType={setMortgageType}
                    mortgageType={mortgageType}
                    authState={authState}
                    application={application}
                    complete={complete}
                    refresh={refresh}
                />
            </Container>
        </>
    );
};
